import { useState, useEffect } from 'react';
import { toast } from '../../../components/ui/toaster';
import { Sparkles, Check, CreditCard, User, LogOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { PRO_FEATURES, type ProFeature } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useAnalytics } from '../../analytics';
import { AuthDialog } from './AuthDialog';
import type { PricingPlan } from '../services/subscriptionService';
import { recordAuthIntentSource } from '../services/signInIntent';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
interface UpgradeDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional: highlight a specific feature that triggered the dialog */
  feature?: ProFeature;
}

/** List of Pro benefits to display */
const PRO_BENEFITS = [
  'Commercial use license',
  'AI Workspace with schema sync and context files',
  'Multiple named sessions with full query history',
  'Unlimited data & schema compare (no row/object caps)',
  'Large database backup, restore, clone & export',
  'Advanced schema overview with custom object selection',
  'Priority support',
];

/** Pricing information */
const PRICING = {
  monthly: {
    price: 9.99,
    label: 'Monthly',
    period: '/month',
  },
  yearly: {
    price: 99,
    label: 'Yearly',
    period: '/year',
    savings: 'Save 17%',
  },
};

/**
 * Dialog explaining Pro features and prompting user to upgrade.
 * Integrates with Stripe checkout for subscription purchases.
 */
export function UpgradeDialog({ open, onOpenChange, feature }: UpgradeDialogProps) {
  const featureInfo = feature ? PRO_FEATURES[feature] : null;
  const { user, signOut } = useAuth();
  const { track } = useAnalytics();

  useEffect(() => {
    if (open) {
      track('ui:upgrade_dialog_shown', { feature: feature ?? 'general' });
    }
  }, [open, track, feature]);
  const {
    startCheckout,
    fetchSubscription,
    isCheckingOut,
    error,
    clearError,
  } = useSubscriptionStore();
 
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan>('yearly');
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<PricingPlan | null>(null);
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false);
  const [confirmationSecondsRemaining, setConfirmationSecondsRemaining] = useState(90);

  const startConfirmationPolling = async () => {
    setIsWaitingForConfirmation(true);
    setConfirmationSecondsRemaining(90);

    const startTime = Date.now();
    const timeoutMs = 90_000;
    const pollIntervalMs = 2_500;

    // We keep the dialog open and poll until subscription becomes active.
    // This is purely UX: the source of truth is the webhook + database.
    while (Date.now() - startTime < timeoutMs) {
      // Update countdown
      const secondsRemaining = Math.max(0, Math.ceil((timeoutMs - (Date.now() - startTime)) / 1000));
      setConfirmationSecondsRemaining(secondsRemaining);

      await fetchSubscription();

      const currentSub = useSubscriptionStore.getState().subscription;
      if (currentSub?.status === 'active' || currentSub?.status === 'trialing') {
        setIsWaitingForConfirmation(false);
        toast.success({
          title: 'Pro activated',
          description: 'Your subscription is active. Thanks for supporting Jam SQL Studio!',
        });
        onOpenChange(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    // Timeout: keep dialog open but stop spinner and show guidance.
    setIsWaitingForConfirmation(false);
    toast.warning({
      title: 'Still waiting for Stripe confirmation',
      description: 'If you just completed checkout, Stripe webhooks may take a moment. Keep the app open and it should update automatically (or open Settings → License and click Manage Subscription to verify).',
      duration: 8000,
    });

    // Keep dialog open so user can retry subscription fetch.
    // The next focus/visibility change will auto-refresh as well.
  };

  const handleSubscribe = async (plan: PricingPlan) => {
    clearError();

    // If not authenticated, show auth dialog first
    if (!user) {
      recordAuthIntentSource('upgrade_dialog');
      setPendingPlan(plan);
      setShowAuthDialog(true);
      return;
    }

    // Start Stripe checkout (opens browser)
    const result = await startCheckout(plan);
    if (result.success) {
      toast.default({
        title: 'Checkout opened',
        description: 'Complete payment in your browser. We will confirm and update Pro status automatically.',
      });
      void startConfirmationPolling();
    } else if (result.errorCode === 'ALREADY_SUBSCRIBED') {
      // User already has an active subscription on the server — refresh local state
      clearError();
      await fetchSubscription();
      const currentSub = useSubscriptionStore.getState().subscription;
      if (currentSub?.status === 'active' || currentSub?.status === 'trialing') {
        toast.success({
          title: 'Pro already active',
          description: 'Your subscription is active. Refreshed your status.',
        });
        onOpenChange(false);
      } else {
        // Refresh didn't resolve it — prompt re-login
        useAuthStore.getState().setUser(null);
        recordAuthIntentSource('upgrade_dialog');
        setPendingPlan(plan);
        setShowAuthDialog(true);
      }
    } else if (result.error?.toLowerCase().includes('not authenticated') || result.error?.toLowerCase().includes('no access token')) {
      // Token expired — clear stale user and prompt re-auth
      useAuthStore.getState().setUser(null);
      clearError();
      recordAuthIntentSource('upgrade_dialog');
      setPendingPlan(plan);
      setShowAuthDialog(true);
    }
  };

  const handleAuthSuccess = async () => {
    setShowAuthDialog(false);

    // If there's a pending plan, start checkout
    if (pendingPlan) {
      const result = await startCheckout(pendingPlan);
      if (result.success) {
        toast.default({
          title: 'Checkout opened',
          description: 'Complete payment in your browser. We will confirm and update Pro status automatically.',
        });
        void startConfirmationPolling();
      }
      setPendingPlan(null);
    }
  };

  const handleAuthDialogClose = (isOpen: boolean) => {
    if (!isOpen) {
      setPendingPlan(null);
    }
    setShowAuthDialog(isOpen);
  };

  const handleClose = () => {
    clearError();
    setIsWaitingForConfirmation(false);
    onOpenChange(false);
  };

  return (
    <>
       <Dialog
         open={open}
         onOpenChange={(nextOpen) => {
           // Keep dialog open while we're waiting for Stripe webhook confirmation.
           // Users can still dismiss manually via the "Maybe Later" button.
           if (!nextOpen && isWaitingForConfirmation) {
             return;
           }
           onOpenChange(nextOpen);
         }}
       >
        <DialogContent className="sm:max-w-[500px]" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Upgrade to Pro
            </DialogTitle>
            <DialogDescription>
              {featureInfo
                ? `${featureInfo.name} is available with Jam SQL Studio Pro.`
                : 'Unlock powerful features with Jam SQL Studio Pro.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {/* Benefits */}
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                What you&apos;ll get:
              </p>
              <ul className="space-y-2">
                {PRO_BENEFITS.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing Options */}
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Choose your plan:</p>
              <div className="grid grid-cols-2 gap-3">
                {/* Monthly Plan */}
                <button
                  type="button"
                  onClick={() => setSelectedPlan('monthly')}
                  className={`relative rounded-lg border-2 p-4 text-left transition-colors ${
                    selectedPlan === 'monthly'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="plan-monthly"
                >
                  <div className="text-sm font-medium">{PRICING.monthly.label}</div>
                  <div className="mt-1">
                    <span className="text-2xl font-bold">${PRICING.monthly.price}</span>
                    <span className="text-sm text-muted-foreground">
                      {PRICING.monthly.period}
                    </span>
                  </div>
                </button>

                {/* Yearly Plan */}
                <button
                  type="button"
                  onClick={() => setSelectedPlan('yearly')}
                  className={`relative rounded-lg border-2 p-4 text-left transition-colors ${
                    selectedPlan === 'yearly'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="plan-yearly"
                >
                  {PRICING.yearly.savings && (
                    <span className="absolute -top-2 right-2 rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                      {PRICING.yearly.savings}
                    </span>
                  )}
                  <div className="text-sm font-medium">{PRICING.yearly.label}</div>
                  <div className="mt-1">
                    <span className="text-2xl font-bold">${PRICING.yearly.price}</span>
                    <span className="text-sm text-muted-foreground">
                      {PRICING.yearly.period}
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Signed-in User Info */}
            {user && (
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Subscribing as:</span>
                  <span className="font-medium">{user.email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => signOut()}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  disabled={isCheckingOut || isWaitingForConfirmation}
                >
                  <LogOut className="h-3 w-3" />
                  Switch
                </button>
              </div>
            )}

            {/* Confirmation / Error */}
            {isWaitingForConfirmation ? (
              <div className="rounded-md border border-border bg-muted/20 px-4 py-3 text-sm">
                <div className="flex items-center gap-2">
                  <RandomDotmSpinner size={16} className="text-muted-foreground" />
                  <span className="font-medium">Waiting for confirmation…</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  This usually takes a few seconds. Remaining: {confirmationSecondsRemaining}s
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  You can close this dialog and keep using the app. We’ll update your Pro status automatically.
                </div>
              </div>
            ) : error ? (
              <div className="text-sm text-destructive">
                {error}
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isCheckingOut}
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => handleSubscribe(selectedPlan)}
              disabled={isCheckingOut || isWaitingForConfirmation}
              data-testid="subscribe-button"
            >
              {isCheckingOut || isWaitingForConfirmation ? (
                <>
                  <RandomDotmSpinner size={16} className="mr-2" />
                  {isWaitingForConfirmation ? 'Confirming...' : 'Processing...'}
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Subscribe Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auth Dialog for unauthenticated users */}
      <AuthDialog
        open={showAuthDialog}
        onOpenChange={handleAuthDialogClose}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
}
